import { useLingui } from '@lingui/react/macro';
import { type FC, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'wouter';
import { createAuthNextPath, useHasAccessToken } from '@/auth/api';
import type { WealthCreateConvertParams } from '@/backend';
import { useLocation } from '@/base/router';
import { Button } from '@/shared/Button';
import { Coin } from '@/shared/Coin';
import { FormErrors, setFormErrors } from '@/shared/FormErrors';
import { useBalance } from '../../wallet/api';
import { useCalculatePrice, useConvert } from './api';
import { BalanceFooter } from './BalanceFooter';
import { CoinPriceInput } from './CoinPriceInput';
import { ConvertConfirmationModal } from './ConfirmDialog';
import { PriceSummaryFooter } from './PriceSummaryFooter';
import { useCoinOptions } from './useCoinOptions';

export const ConvertForm: FC<{
  type: 'buy' | 'sell';
  fromCoin: string;
  onFromCoinChange: (newCoin: string) => void;
  toCoin: string;
  onToCoinChange: (newCoin: string) => void;
  onCustomSubmit?: () => void;
  onSuccess?: () => void;
}> = ({
  type,
  fromCoin,
  onFromCoinChange,
  toCoin,
  onToCoinChange,
  onCustomSubmit,
  onSuccess,
}) => {
  const { t } = useLingui();
  const [location, navigate] = useLocation();
  const formEl = useRef<HTMLFormElement>(null);
  const hasAccessToken = useHasAccessToken();

  const [, setSearchParams] = useSearchParams();
  const convert = useConvert();

  const {
    control,
    handleSubmit,
    watch,
    setError,
    formState,
    reset,
    setValue,
    clearErrors,
  } = useForm<Omit<WealthCreateConvertParams, 'from_coin' | 'to_coin'>>({
    reValidateMode: 'onBlur',
  });

  const fromAmount = watch('from_amount') ?? '0';
  const toAmount = watch('to_amount') ?? '0';

  const [lastTouch, setLastTouch] = useState<'from' | 'to'>();

  const coinOptions = useCoinOptions(type);

  const fromBalance = useBalance(fromCoin);

  const serverPrices = useCalculatePrice(
    fromCoin,
    toCoin,
    !lastTouch ? undefined : lastTouch === 'from' ? fromAmount : toAmount,
    lastTouch,
  );

  /* Reset From Amount When Balance Fetches */
  useEffect(() => {
    if (fromBalance.data?.balance) {
      setValue('from_amount', fromBalance.data.balance);
    }
    setLastTouch('from');
  }, [fromBalance.data?.balance, setValue]);

  /* Revalidate Target Amount Input from Server */
  useEffect(() => {
    if (lastTouch === 'from' && serverPrices.data?.to_amount)
      setValue('to_amount', serverPrices.data.to_amount);
    if (lastTouch === 'to' && serverPrices.data?.from_amount)
      setValue('from_amount', serverPrices.data.from_amount);
  }, [
    lastTouch,
    serverPrices.data?.from_amount,
    serverPrices.data?.to_amount,
    setValue,
  ]);

  useEffect(() => {
    if (type || fromCoin || toCoin) {
      clearErrors();
    }
  }, [type, fromCoin, toCoin, clearErrors]);

  return (
    <>
      <form
        ref={formEl}
        onSubmit={handleSubmit((data) => {
          if (!hasAccessToken) {
            navigate(
              'app',
              `/auth?next=${createAuthNextPath('app', location)}`,
            );
            return;
          }
          return onCustomSubmit
            ? onCustomSubmit()
            : convert.trigger(
                {
                  from_coin: fromCoin,
                  to_coin: toCoin,
                  from_amount: data.from_amount!,
                },
                {
                  onError: setFormErrors(setError),
                  onSuccess: (data) => {
                    setSearchParams((s) => {
                      s.set('id', data.tracking_code);
                      return s;
                    });
                    setValue('to_amount', data.to_amount);
                  },
                },
              );
        })}
      >
        <Controller
          control={control}
          name="from_amount"
          render={({ field }) => (
            <CoinPriceInput
              coin={fromCoin}
              coins={coinOptions.data.paybale ?? []}
              onCoinChange={(n) => {
                setLastTouch(undefined);
                onFromCoinChange(n);
              }}
              price={field.value}
              onPriceChange={(n) => {
                setLastTouch('from');
                field.onChange(n);
              }}
              label={t`You Pay`}
              loading={
                (lastTouch === 'to' && serverPrices.isLoading) || !lastTouch
              }
              className="mb-widget"
              footer={
                <BalanceFooter
                  coin={fromCoin}
                  onPriceChange={(n) => {
                    setLastTouch('from');
                    field.onChange(n);
                  }}
                />
              }
            />
          )}
        />

        <Controller
          control={control}
          name="to_amount"
          render={({ field }) => (
            <CoinPriceInput
              coin={toCoin}
              coins={coinOptions.data.receivable ?? []}
              onCoinChange={onToCoinChange}
              price={field.value}
              onPriceChange={(n) => {
                setLastTouch('to');
                field.onChange(n);
              }}
              label={t`You Receive`}
              loading={
                (lastTouch === 'from' && serverPrices.isLoading) || !lastTouch
              }
              className="mb-widget"
              footer={
                <PriceSummaryFooter fromCoin={fromCoin} toCoin={toCoin} />
              }
            />
          )}
        />

        <Button
          size={12}
          type="submit"
          variant={type === 'buy' ? 'positive' : 'negative'}
          loading={convert.isMutating}
          disabled={+fromAmount === 0 || +toAmount === 0}
          block
          className="w-full"
          data-submit="true"
        >
          {type === 'buy' ? t`Buy` : t`Sell`}
          <Coin
            size={6}
            coin={type === 'buy' ? toCoin : fromCoin}
            variant="symbol"
          />
        </Button>
        <FormErrors className="mt-4" errors={formState.errors} />
      </form>

      <ConvertConfirmationModal
        type={type}
        data={
          !convert.isMutating &&
          formState.isSubmitSuccessful &&
          formState.isSubmitted &&
          convert.data
            ? convert.data
            : undefined
        }
        onRefresh={() => {
          const submitBtn =
            formEl.current?.querySelector<HTMLButtonElement>('[data-submit]');
          if (submitBtn) submitBtn.click();
        }}
        onCancel={() =>
          reset(undefined, {
            keepValues: true,
            keepIsSubmitSuccessful: false,
            keepIsSubmitted: false,
          })
        }
        onFinish={() => {
          reset(undefined, {
            keepValues: true,
            keepIsSubmitSuccessful: false,
            keepIsSubmitted: false,
          });
          onSuccess?.();
        }}
      />
    </>
  );
};
